import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Globe, Navigation } from "lucide-react";
import { NAV_LINKS, CONTACT, MAPS_DIRECTIONS } from "@/lib/site";
import BrochureButton from "@/components/BrochureButton";
import Accreditations from "@/components/Accreditations";
import logo from "@/assets/logo.png";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-brand-blue text-white">
      {/* Watermark */}
      <span className="pointer-events-none select-none absolute -bottom-10 -right-6 text-[22vw] font-heading font-extrabold leading-none text-white/[0.04]">
        Gowell
      </span>

      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="inline-flex bg-white rounded-xl p-3">
              <img src={logo} alt="Fly Gowell" className="h-20 w-auto object-contain" />
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/70">
              Your corporate mobility partner for seamless ticketing, visas, documentation and
              executive travel, all handled with precision.
            </p>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-white/60">
              Quick Links
            </h4>
            <ul className="mt-5 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.path}>
                  <Link
                    to={l.path}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="text-sm text-white/75 hover:text-brand-yellow transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-5">
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-white/60">
              Get in Touch
            </h4>
            <ul className="mt-5 space-y-4 text-sm text-white/80">
              <li className="flex gap-3">
                <MapPin size={18} className="mt-0.5 shrink-0 text-brand-green" />
                <span>{CONTACT.address}</span>
              </li>
              <li className="flex gap-3">
                <Phone size={18} className="mt-0.5 shrink-0 text-brand-green" />
                <span>
                  {CONTACT.phones.map((p, i) => (
                    <a key={p} href={`tel:+91${p}`} className="hover:text-brand-yellow">
                      {p}{i < CONTACT.phones.length - 1 ? ", " : ""}
                    </a>
                  ))}
                  {CONTACT.landline && (
                    <>
                      <br />
                      <span className="text-white/60 text-xs uppercase tracking-widest mr-1">Landline</span>
                      <a href={`tel:${CONTACT.landline.replace(/[^+\d]/g, "")}`} className="hover:text-brand-yellow">{CONTACT.landline}</a>
                    </>
                  )}
                </span>
              </li>
              <li className="flex gap-3">
                <Mail size={18} className="mt-0.5 shrink-0 text-brand-green" />
                <span>
                  {CONTACT.emails.map((e, i) => (
                    <a key={e} href={`mailto:${e}`} className="hover:text-brand-yellow break-all">
                      {e}{i < CONTACT.emails.length - 1 ? ", " : ""}
                    </a>
                  ))}
                </span>
              </li>
              <li className="flex gap-3">
                <Globe size={18} className="mt-0.5 shrink-0 text-brand-green" />
                <a href={`https://${CONTACT.website}`} target="_blank" rel="noreferrer" className="hover:text-brand-yellow">
                  {CONTACT.website}
                </a>
              </li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={MAPS_DIRECTIONS}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-directions"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-sm font-medium text-white hover:bg-white hover:text-brand-blue transition-colors"
              >
                <Navigation size={16} /> Get Directions
              </a>
              <BrochureButton variant="outline" label="Download Brochure" className="!px-5 !py-2.5 !text-sm border !border-white/25" />
            </div>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-10">
          <Accreditations variant="dark" />
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/10 pt-6">
          <p className="text-sm text-white/60">© Fly Gowell Travel Solutions. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
